import React, { useState, useEffect, useCallback } from 'react';
import { IconClose } from './Icons';
import './AdminDashboardPanel.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';
const ADMIN_TOKEN_KEY = 'symphonate-admin-token';

function authHeaders() {
  const token = localStorage.getItem('symphonate-token');
  return { Authorization: `Bearer ${token}` };
}

// Headers for the gated data endpoints: login token + the short-lived admin
// step-up token minted after an MFA challenge.
function adminHeaders(adminToken) {
  return { ...authHeaders(), ...(adminToken ? { 'X-Admin-Token': adminToken } : {}) };
}

function formatTimestamp(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(iso);
  }
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return String(iso);
  }
}

// Tokens read nicer as compact "12.3k" once they get large.
function formatNumber(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return String(v);
}

// AI spend arrives in micro-dollars (millionths of a USD) so sub-cent cheap-model
// calls still add up; show dollars and cents, and "<$0.01" rather than a
// misleading "$0.00" when there is some spend.
function formatUsd(micros) {
  const v = Number(micros) || 0;
  if (v > 0 && v < 10_000) return '<$0.01';
  return `$${(v / 1_000_000).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// "2026-10" → "Oct 2026" (UTC, so the month never shifts with the viewer's zone).
function formatMonth(month) {
  const [y, m] = String(month || '')
    .split('-')
    .map(Number);
  if (!y || !m) return String(month || '—');
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

const TABS = [
  { id: 'users', label: 'Signups' },
  { id: 'leads', label: 'Leads' },
  { id: 'blocked', label: 'Blocked emails' },
  { id: 'workspaces', label: 'Workspaces' },
  { id: 'usageUser', label: 'Usage / user' },
  { id: 'usageThread', label: 'Usage / thread' },
  { id: 'aiSpend', label: 'AI spend' },
  { id: 'tagHealth', label: 'Capture health' },
];

const WINDOWS = [
  { id: 'month', label: 'This month' },
  { id: 'all', label: 'All time' },
];

/**
 * Platform admin dashboard — a deployment-wide operator view (strategy.md §6
 * item 9): who signed up, what they built, and how much AI spend they drive,
 * with the ability to remove an account. Visible only to platform admins
 * (PLATFORM_ADMIN_EMAILS), and gated behind a second factor: TOTP (primary)
 * with an email-code fallback. The server enforces all of this too — this UI
 * just drives the enrollment/challenge flow before showing any data.
 */
export default function AdminDashboardPanel({ currentUserId, onClose }) {
  // 'loading' | 'enroll' | 'challenge' | 'ready'
  const [phase, setPhase] = useState('loading');
  const [gateError, setGateError] = useState(null);
  const [adminToken, setAdminToken] = useState(
    () => window.sessionStorage.getItem(ADMIN_TOKEN_KEY) || '',
  );
  const [emailFallbackAvailable, setEmailFallbackAvailable] = useState(false);

  // Enrollment state.
  const [setupData, setSetupData] = useState(null); // { secret, otpauthUrl }
  const [enrollCode, setEnrollCode] = useState('');
  const [busy, setBusy] = useState(false);

  // Challenge state.
  const [challengeCode, setChallengeCode] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  // Dashboard data state.
  const [data, setData] = useState(null);
  const [dataError, setDataError] = useState(null);
  const [tab, setTab] = useState('users');
  const [windowId, setWindowId] = useState('month');
  const [deletingId, setDeletingId] = useState(null);
  const [blockingId, setBlockingId] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [newBlockEmail, setNewBlockEmail] = useState('');
  const [newBlockReason, setNewBlockReason] = useState('');
  const [blockBusy, setBlockBusy] = useState(false);

  const persistAdminToken = useCallback((t) => {
    setAdminToken(t);
    if (t) window.sessionStorage.setItem(ADMIN_TOKEN_KEY, t);
    else window.sessionStorage.removeItem(ADMIN_TOKEN_KEY);
  }, []);

  // Figure out which gate screen to show on open (and after token expiry).
  const checkStatus = useCallback(async () => {
    setPhase('loading');
    setGateError(null);
    try {
      const res = await fetch(`${SERVER_URL}/api/admin/mfa/status`, {
        headers: adminHeaders(window.sessionStorage.getItem(ADMIN_TOKEN_KEY) || ''),
      });
      if (res.status === 403) throw new Error('Platform admin access required');
      if (!res.ok) throw new Error('Could not check admin access');
      const s = await res.json();
      setEmailFallbackAvailable(!!s.emailFallbackAvailable);
      if (!s.enrolled) {
        setPhase('enroll');
      } else if (s.hasSession) {
        setPhase('ready');
      } else {
        persistAdminToken('');
        setPhase('challenge');
      }
    } catch (e) {
      setGateError(e.message || 'Could not check admin access');
      setPhase('error');
    }
  }, [persistAdminToken]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // --- Enrollment ---

  const beginSetup = useCallback(async () => {
    setBusy(true);
    setGateError(null);
    try {
      const res = await fetch(`${SERVER_URL}/api/admin/mfa/setup`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Could not start setup');
      setSetupData(body);
    } catch (e) {
      setGateError(e.message || 'Could not start setup');
    } finally {
      setBusy(false);
    }
  }, []);

  const confirmEnroll = useCallback(
    async (e) => {
      e.preventDefault();
      setBusy(true);
      setGateError(null);
      try {
        const res = await fetch(`${SERVER_URL}/api/admin/mfa/enable`, {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: enrollCode.trim() }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || 'Invalid code');
        persistAdminToken(body.adminToken);
        setEnrollCode('');
        setSetupData(null);
        setPhase('ready');
      } catch (err) {
        setGateError(err.message || 'Invalid code');
      } finally {
        setBusy(false);
      }
    },
    [enrollCode, persistAdminToken],
  );

  // --- Challenge ---

  const sendEmailCode = useCallback(async () => {
    setBusy(true);
    setGateError(null);
    try {
      const res = await fetch(`${SERVER_URL}/api/admin/mfa/email-code`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Could not send email code');
      setEmailSent(true);
    } catch (e) {
      setGateError(e.message || 'Could not send email code');
    } finally {
      setBusy(false);
    }
  }, []);

  const submitChallenge = useCallback(
    async (method) => {
      setBusy(true);
      setGateError(null);
      try {
        const res = await fetch(`${SERVER_URL}/api/admin/mfa/verify`, {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ method, code: challengeCode.trim() }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || 'Invalid code');
        persistAdminToken(body.adminToken);
        setChallengeCode('');
        setEmailSent(false);
        setPhase('ready');
      } catch (e) {
        setGateError(e.message || 'Invalid code');
      } finally {
        setBusy(false);
      }
    },
    [challengeCode, persistAdminToken],
  );

  // --- Dashboard data ---

  const load = useCallback(async () => {
    setDataError(null);
    try {
      const since = windowId === 'all' ? 'all' : '';
      const qs = since ? `?since=${since}` : '';
      const res = await fetch(`${SERVER_URL}/api/admin/overview${qs}`, {
        headers: adminHeaders(adminToken),
      });
      if (res.status === 401) {
        // Admin session expired — drop back to the challenge screen.
        const body = await res.json().catch(() => ({}));
        if (body.code === 'mfa_challenge_required') {
          persistAdminToken('');
          setPhase('challenge');
          return;
        }
      }
      if (!res.ok) throw new Error('Failed to load admin dashboard');
      setData(await res.json());
    } catch (e) {
      setDataError(e.message || 'Failed to load admin dashboard');
    }
  }, [windowId, adminToken, persistAdminToken]);

  useEffect(() => {
    if (phase === 'ready') load();
  }, [phase, load]);

  const handleDelete = useCallback(
    async (u) => {
      const ok = window.confirm(
        `Delete ${u.name} (${u.email})?\n\n` +
          'Their workspace memberships are removed. Any workspace where they were ' +
          'the only member is deleted along with its threads. This cannot be undone.',
      );
      if (!ok) return;
      setDeletingId(u.id);
      try {
        const res = await fetch(`${SERVER_URL}/api/admin/users/${u.id}`, {
          method: 'DELETE',
          headers: adminHeaders(adminToken),
        });
        if (res.status === 401) {
          persistAdminToken('');
          setPhase('challenge');
          return;
        }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Failed to delete user');
        }
        await load();
      } catch (e) {
        window.alert(e.message || 'Failed to delete user');
      } finally {
        setDeletingId(null);
      }
    },
    [adminToken, load, persistAdminToken],
  );

  const handleBlock = useCallback(
    async (u) => {
      const ok = window.confirm(
        `Revoke access for ${u.name} (${u.email})?\n\n` +
          'They are signed out immediately and can no longer log in, and their email ' +
          'is blocked from signing up again. Their account and content are preserved. ' +
          'You can restore access later.',
      );
      if (!ok) return;
      setBlockingId(u.id);
      try {
        const res = await fetch(`${SERVER_URL}/api/admin/users/${u.id}/block`, {
          method: 'POST',
          headers: { ...adminHeaders(adminToken), 'Content-Type': 'application/json' },
          body: JSON.stringify({ blockEmail: true }),
        });
        if (res.status === 401) {
          persistAdminToken('');
          setPhase('challenge');
          return;
        }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Failed to revoke access');
        }
        await load();
      } catch (e) {
        window.alert(e.message || 'Failed to revoke access');
      } finally {
        setBlockingId(null);
      }
    },
    [adminToken, load, persistAdminToken],
  );

  const handleUnblock = useCallback(
    async (u) => {
      setBlockingId(u.id);
      try {
        const res = await fetch(`${SERVER_URL}/api/admin/users/${u.id}/unblock`, {
          method: 'POST',
          headers: { ...adminHeaders(adminToken), 'Content-Type': 'application/json' },
          body: JSON.stringify({ unblockEmail: true }),
        });
        if (res.status === 401) {
          persistAdminToken('');
          setPhase('challenge');
          return;
        }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Failed to restore access');
        }
        await load();
      } catch (e) {
        window.alert(e.message || 'Failed to restore access');
      } finally {
        setBlockingId(null);
      }
    },
    [adminToken, load, persistAdminToken],
  );

  const handleAddBlockedEmail = useCallback(
    async (e) => {
      e.preventDefault();
      const email = newBlockEmail.trim().toLowerCase();
      if (!email) return;
      setBlockBusy(true);
      try {
        const res = await fetch(`${SERVER_URL}/api/admin/blocked-emails`, {
          method: 'POST',
          headers: { ...adminHeaders(adminToken), 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, reason: newBlockReason.trim() || undefined }),
        });
        if (res.status === 401) {
          persistAdminToken('');
          setPhase('challenge');
          return;
        }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Failed to block email');
        }
        setNewBlockEmail('');
        setNewBlockReason('');
        await load();
      } catch (err) {
        window.alert(err.message || 'Failed to block email');
      } finally {
        setBlockBusy(false);
      }
    },
    [adminToken, newBlockEmail, newBlockReason, load, persistAdminToken],
  );

  const handleUnblockEmail = useCallback(
    async (email) => {
      try {
        const res = await fetch(
          `${SERVER_URL}/api/admin/blocked-emails/${encodeURIComponent(email)}`,
          { method: 'DELETE', headers: adminHeaders(adminToken) },
        );
        if (res.status === 401) {
          persistAdminToken('');
          setPhase('challenge');
          return;
        }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Failed to unblock email');
        }
        await load();
      } catch (e) {
        window.alert(e.message || 'Failed to unblock email');
      }
    },
    [adminToken, load, persistAdminToken],
  );

  const users = data?.users || [];
  const blockedEmails = data?.blockedEmails || [];
  const leads = data?.leads || [];
  const userSearchTerm = userSearch.trim().toLowerCase();
  const filteredUsers = userSearchTerm
    ? users.filter(
        (u) =>
          (u.email || '').toLowerCase().includes(userSearchTerm) ||
          (u.name || '').toLowerCase().includes(userSearchTerm),
      )
    : users;
  const workspaces = data?.workspaces || [];
  const usageByUser = data?.usageByUser || [];
  const usageByThread = data?.usageByThread || [];
  // T2a capture health. `parsed` + `dropped` are cumulative tag counts for this
  // server process since its last restart; `byTag` breaks the drops down by
  // keyword. A drop is a tag the AI opened but malformed, so it produced no
  // captured object at all — see the hint in the tab body.
  const tagStats = data?.aiTagStats || { parsed: 0, dropped: 0, byTag: {} };
  // Per-AI spend: to-date totals per provider plus a month-by-month breakdown,
  // all-time (it ignores the window selector).
  const aiSpend = data?.aiSpend || { providers: [], months: [], totalMicros: 0 };
  const spendProviders = aiSpend.providers || [];
  const spendMonths = aiSpend.months || [];
  const tagTotal = (tagStats.parsed || 0) + (tagStats.dropped || 0);
  const tagDropRate = tagTotal > 0 ? (tagStats.dropped / tagTotal) * 100 : 0;
  const tagDropRows = Object.entries(tagStats.byTag || {}).sort((a, b) => b[1] - a[1]);

  function renderGate(inner) {
    return (
      <div className="admin-gate">
        {gateError && <div className="admin-gate-error">{gateError}</div>}
        {inner}
      </div>
    );
  }

  return (
    <div className="admin-backdrop" onClick={onClose} role="presentation">
      <div
        className="admin-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Admin dashboard"
      >
        <div className="admin-header">
          <div>
            <h2>Admin Dashboard</h2>
            <p className="admin-subtitle">
              {phase === 'ready' ? 'Deployment-wide operator view' : 'Secured with MFA'}
            </p>
          </div>
          <button className="admin-close" onClick={onClose} aria-label="Close admin dashboard">
            <IconClose size={18} />
          </button>
        </div>

        {phase === 'loading' && <div className="admin-empty">Checking access…</div>}

        {phase === 'error' &&
          renderGate(
            <div className="admin-gate-box">
              <p className="admin-gate-lead">Couldn’t verify admin access.</p>
              <button className="btn-admin-primary" onClick={checkStatus}>
                Retry
              </button>
            </div>,
          )}

        {phase === 'enroll' &&
          renderGate(
            <div className="admin-gate-box">
              <p className="admin-gate-lead">Set up two-factor authentication</p>
              {!setupData ? (
                <>
                  <p className="admin-gate-hint">
                    The admin dashboard requires a second factor. Add it to an authenticator app
                    (Google Authenticator, 1Password, Authy…) to continue.
                  </p>
                  <button className="btn-admin-primary" onClick={beginSetup} disabled={busy}>
                    {busy ? 'Starting…' : 'Begin setup'}
                  </button>
                </>
              ) : (
                <>
                  <p className="admin-gate-hint">
                    Add this secret to your authenticator app (scan the URI or key it in manually),
                    then enter the 6-digit code it shows.
                  </p>
                  <div className="admin-secret-block">
                    <span className="admin-secret-label">Secret</span>
                    <code className="admin-secret">{setupData.secret}</code>
                  </div>
                  <div className="admin-secret-block">
                    <span className="admin-secret-label">otpauth URI</span>
                    <code className="admin-secret admin-secret-uri">{setupData.otpauthUrl}</code>
                  </div>
                  <form onSubmit={confirmEnroll} className="admin-code-form">
                    <input
                      className="admin-code-input"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="123456"
                      maxLength={6}
                      value={enrollCode}
                      onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, ''))}
                      autoFocus
                    />
                    <button
                      type="submit"
                      className="btn-admin-primary"
                      disabled={busy || enrollCode.length !== 6}
                    >
                      {busy ? 'Verifying…' : 'Verify & enable'}
                    </button>
                  </form>
                </>
              )}
            </div>,
          )}

        {phase === 'challenge' &&
          renderGate(
            <div className="admin-gate-box">
              <p className="admin-gate-lead">Enter your authentication code</p>
              <p className="admin-gate-hint">
                Open your authenticator app and enter the current 6-digit code for Symphonate.
              </p>
              <div className="admin-code-form">
                <input
                  className="admin-code-input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  maxLength={6}
                  value={challengeCode}
                  onChange={(e) => setChallengeCode(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && challengeCode.length === 6) submitChallenge('totp');
                  }}
                  autoFocus
                />
                <button
                  type="button"
                  className="btn-admin-primary"
                  disabled={busy || challengeCode.length !== 6}
                  onClick={() => submitChallenge('totp')}
                >
                  {busy ? 'Verifying…' : 'Verify'}
                </button>
              </div>

              {emailFallbackAvailable && (
                <div className="admin-fallback">
                  <div className="admin-fallback-divider">
                    <span>can’t use your app?</span>
                  </div>
                  {!emailSent ? (
                    <button
                      type="button"
                      className="btn-admin-link"
                      onClick={sendEmailCode}
                      disabled={busy}
                    >
                      Email me a code instead
                    </button>
                  ) : (
                    <div className="admin-code-form">
                      <span className="admin-gate-hint admin-gate-hint-sent">
                        We emailed a code to your admin address. Enter it above, then:
                      </span>
                      <button
                        type="button"
                        className="btn-admin-primary"
                        disabled={busy || challengeCode.length !== 6}
                        onClick={() => submitChallenge('email')}
                      >
                        {busy ? 'Verifying…' : 'Verify email code'}
                      </button>
                      <button
                        type="button"
                        className="btn-admin-link"
                        onClick={sendEmailCode}
                        disabled={busy}
                      >
                        Resend
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>,
          )}

        {phase === 'ready' && (
          <>
            <div className="admin-toolbar">
              <div className="admin-tabs">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    className={`admin-tab ${tab === t.id ? 'active' : ''}`}
                    onClick={() => setTab(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              {(tab === 'usageUser' || tab === 'usageThread') && (
                <div className="admin-window">
                  {WINDOWS.map((w) => (
                    <button
                      key={w.id}
                      className={`admin-window-btn ${windowId === w.id ? 'active' : ''}`}
                      onClick={() => setWindowId(w.id)}
                    >
                      {w.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {dataError && <div className="admin-empty admin-error">{dataError}</div>}

            {!dataError && (
              <div className="admin-body">
                {tab === 'users' && (
                  <>
                    <div className="admin-search-row">
                      <input
                        className="admin-search-input"
                        type="search"
                        placeholder="Search by name or email…"
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                      />
                    </div>
                    <p className="admin-hint">
                      {filteredUsers.length} of {users.length} signup{users.length === 1 ? '' : 's'}
                      {userSearchTerm ? ' matching' : '. Newest first.'}
                    </p>
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Status</th>
                          <th>Signed up</th>
                          <th>Last seen</th>
                          <th className="num">Workspaces</th>
                          <th className="num">Threads</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.length === 0 && (
                          <tr>
                            <td colSpan={8} className="admin-empty-row">
                              No users match “{userSearch}”.
                            </td>
                          </tr>
                        )}
                        {filteredUsers.map((u) => (
                          <tr key={u.id} className={u.blockedAt ? 'admin-row-blocked' : ''}>
                            <td>{u.name}</td>
                            <td className="admin-mono">{u.email}</td>
                            <td>
                              {u.blockedAt ? (
                                <span className="admin-status-blocked">Revoked</span>
                              ) : (
                                <span className="admin-status-active">Active</span>
                              )}
                            </td>
                            <td>{formatDate(u.createdAt)}</td>
                            <td>{formatTimestamp(u.lastSeen)}</td>
                            <td className="num">{u.workspacesCreated}</td>
                            <td className="num">{u.threadsCreated}</td>
                            <td className="admin-row-action">
                              {u.id === currentUserId ? (
                                <span className="admin-you">you</span>
                              ) : (
                                <div className="admin-action-group">
                                  {u.blockedAt ? (
                                    <button
                                      className="admin-restore-btn"
                                      onClick={() => handleUnblock(u)}
                                      disabled={blockingId === u.id}
                                    >
                                      {blockingId === u.id ? 'Restoring…' : 'Restore'}
                                    </button>
                                  ) : (
                                    <button
                                      className="admin-block-btn"
                                      onClick={() => handleBlock(u)}
                                      disabled={blockingId === u.id}
                                    >
                                      {blockingId === u.id ? 'Revoking…' : 'Revoke'}
                                    </button>
                                  )}
                                  <button
                                    className="admin-delete-btn"
                                    onClick={() => handleDelete(u)}
                                    disabled={deletingId === u.id}
                                  >
                                    {deletingId === u.id ? 'Removing…' : 'Remove'}
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {tab === 'leads' && (
                  <>
                    <p className="admin-hint">
                      People who left an email without creating an account — from the sign-in screen
                      or the storefront. Each one is also emailed to the platform admins as it
                      arrives.
                    </p>
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Source</th>
                          <th>Note</th>
                          <th>Left</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leads.length === 0 && (
                          <tr>
                            <td colSpan={4} className="admin-empty-row">
                              No leads yet.
                            </td>
                          </tr>
                        )}
                        {leads.map((l) => (
                          <tr key={l.email}>
                            <td className="admin-mono">{l.email}</td>
                            <td>{l.source || '—'}</td>
                            <td>{l.note || '—'}</td>
                            <td>{formatDate(l.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {tab === 'blocked' && (
                  <>
                    <p className="admin-hint">
                      Emails blocked from signing up. Revoking a user (Signups tab) adds their
                      address here automatically; you can also block an address pre-emptively.
                    </p>
                    <form className="admin-block-form" onSubmit={handleAddBlockedEmail}>
                      <input
                        className="admin-search-input"
                        type="email"
                        placeholder="email@example.com"
                        value={newBlockEmail}
                        onChange={(e) => setNewBlockEmail(e.target.value)}
                        required
                      />
                      <input
                        className="admin-search-input"
                        type="text"
                        placeholder="Reason (optional)"
                        value={newBlockReason}
                        onChange={(e) => setNewBlockReason(e.target.value)}
                      />
                      <button
                        type="submit"
                        className="btn-admin-primary"
                        disabled={blockBusy || !newBlockEmail.trim()}
                      >
                        {blockBusy ? 'Blocking…' : 'Block email'}
                      </button>
                    </form>
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Reason</th>
                          <th>Blocked</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {blockedEmails.length === 0 && (
                          <tr>
                            <td colSpan={4} className="admin-empty-row">
                              No blocked emails.
                            </td>
                          </tr>
                        )}
                        {blockedEmails.map((b) => (
                          <tr key={b.email}>
                            <td className="admin-mono">{b.email}</td>
                            <td>{b.reason || '—'}</td>
                            <td>{formatDate(b.createdAt)}</td>
                            <td className="admin-row-action">
                              <button
                                className="admin-restore-btn"
                                onClick={() => handleUnblockEmail(b.email)}
                              >
                                Unblock
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {tab === 'workspaces' && (
                  <>
                    <p className="admin-hint">
                      {workspaces.length} workspace{workspaces.length === 1 ? '' : 's'}.
                    </p>
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Workspace</th>
                          <th>Owner</th>
                          <th>Plan</th>
                          <th className="num">Members</th>
                          <th className="num">Threads</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workspaces.map((w) => (
                          <tr key={w.id}>
                            <td>{w.name}</td>
                            <td>
                              {w.ownerName || '—'}
                              {w.ownerEmail && <span className="admin-sub">{w.ownerEmail}</span>}
                            </td>
                            <td>
                              <span className={`admin-plan admin-plan-${w.planTier}`}>
                                {w.planTier}
                              </span>
                            </td>
                            <td className="num">{w.memberCount}</td>
                            <td className="num">{w.threadCount}</td>
                            <td>{formatDate(w.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {tab === 'usageUser' && (
                  <>
                    <p className="admin-hint">
                      AI spend per user ({windowId === 'all' ? 'all time' : 'this month'}). Tokens
                      are estimated (≈4 chars/token).
                    </p>
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>User</th>
                          <th className="num">Tokens</th>
                          <th className="num">Tool calls</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usageByUser.length === 0 && (
                          <tr>
                            <td colSpan={3} className="admin-empty-row">
                              No usage in this window.
                            </td>
                          </tr>
                        )}
                        {usageByUser.map((r) => (
                          <tr key={r.userId || 'unknown'}>
                            <td>
                              {r.name || 'Unknown / removed'}
                              {r.email && <span className="admin-sub">{r.email}</span>}
                            </td>
                            <td className="num">{formatNumber(r.tokens)}</td>
                            <td className="num">{formatNumber(r.toolCalls)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {tab === 'usageThread' && (
                  <>
                    <p className="admin-hint">
                      AI spend per thread ({windowId === 'all' ? 'all time' : 'this month'}).
                    </p>
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Thread</th>
                          <th>Workspace</th>
                          <th className="num">Tokens</th>
                          <th className="num">Tool calls</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usageByThread.length === 0 && (
                          <tr>
                            <td colSpan={4} className="admin-empty-row">
                              No usage in this window.
                            </td>
                          </tr>
                        )}
                        {usageByThread.map((r, i) => (
                          <tr key={r.roomId || `unattributed-${i}`}>
                            <td>{r.roomName || 'Unknown / deleted'}</td>
                            <td>{r.workspaceName || '—'}</td>
                            <td className="num">{formatNumber(r.tokens)}</td>
                            <td className="num">{formatNumber(r.toolCalls)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {tab === 'aiSpend' && (
                  <>
                    <p className="admin-hint">
                      Estimated spend per AI, all time
                      {aiSpend.trackingSince
                        ? ` (per-AI tracking since ${formatDate(aiSpend.trackingSince)})`
                        : ''}
                      . Every AI call counts — replies, catch-ups, memory refreshes, briefings and
                      ask-Aimy. Tokens are estimated (≈4 chars/token) and priced at list rates, so
                      treat this as a close guide to your provider bills, not an exact match.
                    </p>
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>AI</th>
                          <th className="num">Calls</th>
                          <th className="num">Tokens</th>
                          <th className="num">Spent to date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {spendProviders.map((p) => (
                          <tr key={p.provider || 'unattributed'}>
                            <td>
                              {p.label}
                              {p.models && p.models.length > 0 && (
                                <span className="admin-sub">{p.models.join(', ')}</span>
                              )}
                            </td>
                            <td className="num">{p.provider ? formatNumber(p.calls) : '—'}</td>
                            <td className="num">{formatNumber(p.tokens)}</td>
                            <td className="num">{formatUsd(p.costMicros)}</td>
                          </tr>
                        ))}
                        <tr className="admin-total-row">
                          <td>Total</td>
                          <td className="num" />
                          <td className="num" />
                          <td className="num">{formatUsd(aiSpend.totalMicros)}</td>
                        </tr>
                      </tbody>
                    </table>
                    <p className="admin-hint">Spent per month</p>
                    <div className="admin-table-scroll">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Month</th>
                            {spendProviders.map((p) => (
                              <th key={p.provider || 'unattributed'} className="num">
                                {p.provider ? p.label : 'Unattributed'}
                              </th>
                            ))}
                            <th className="num">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {spendMonths.length === 0 && (
                            <tr>
                              <td colSpan={spendProviders.length + 2} className="admin-empty-row">
                                No AI spend recorded yet.
                              </td>
                            </tr>
                          )}
                          {spendMonths.map((m) => (
                            <tr key={m.month}>
                              <td className="admin-nowrap">{formatMonth(m.month)}</td>
                              {spendProviders.map((p) => (
                                <td key={p.provider || 'unattributed'} className="num">
                                  {formatUsd((m.byProvider || {})[p.provider] || 0)}
                                </td>
                              ))}
                              <td className="num">{formatUsd(m.totalMicros)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {tab === 'tagHealth' && (
                  <>
                    <p className="admin-hint">
                      How reliably the AI's capture tags parse. A <strong>drop</strong> is a tag the
                      AI opened but malformed, so it produced <strong>no</strong> decision, artifact
                      or to-do — a silent miss in the feature the product is sold on. Counts are for
                      this server process since its last restart, so they reset on every deploy and
                      ignore the window selector above.
                    </p>
                    <table className="admin-table">
                      <tbody>
                        <tr>
                          <td>Tags parsed</td>
                          <td className="num">{formatNumber(tagStats.parsed || 0)}</td>
                        </tr>
                        <tr>
                          <td>Tags dropped (malformed)</td>
                          <td className="num">{formatNumber(tagStats.dropped || 0)}</td>
                        </tr>
                        <tr>
                          <td>Drop rate</td>
                          <td className="num">
                            {tagTotal === 0 ? '—' : `${tagDropRate.toFixed(1)}%`}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <p className="admin-hint">Drops by tag</p>
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Tag</th>
                          <th className="num">Dropped</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tagDropRows.length === 0 && (
                          <tr>
                            <td colSpan={2} className="admin-empty-row">
                              {tagTotal === 0
                                ? 'No AI turns with capture tags since this process started.'
                                : 'No malformed tags — every tag parsed cleanly.'}
                            </td>
                          </tr>
                        )}
                        {tagDropRows.map(([keyword, count]) => (
                          <tr key={keyword}>
                            <td>{keyword}</td>
                            <td className="num">{formatNumber(count)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
